import { useEffect, useState } from "react";
import type { DashboardData } from "../types";
import { api, ApiError, type VeiculoManutencao } from "../api";
import { statusTone } from "../format";
import ErrorState from "./ErrorState";
import { BarChart, PieChart } from "./charts";
import {
  IconGraduation,
  IconRepeat,
  IconHardHat,
  IconSmile,
  IconList,
  IconBuilding,
  IconGauge,
  IconClipboardCheck,
} from "../icons";

const COLORS = ["#0c7a3f", "#15a85a", "#34d27e", "#0a5c30", "#5cd99a", "#1f7a4d", "#8be0b3", "#0e9488"];

/** Cores semânticas por situação de calibração. */
const CALIB_CORES: Record<string, string> = {
  "Válida": "#15a85a",
  "A vencer": "#c98a14",
  "Vencida": "#dc2626",
  "Bloqueado": "#7f1d1d",
  "Sem validade": "#94a3b8",
};

/** Cores semânticas por status de liberação. */
const LIB_CORES: Record<string, string> = {
  "Liberado": "#15a85a",
  "Devolvido": "#0e9488",
  "Pendente": "#c98a14",
  "Bloqueado": "#dc2626",
};

export default function Dashboard({ refreshKey }: { refreshKey: number }) {
  const [data, setData] = useState<DashboardData | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [reload, setReload] = useState(0);
  // Veículos que precisam de manutenção (visão de gestão — todos os responsáveis).
  const [manut, setManut] = useState<VeiculoManutencao[]>([]);

  useEffect(() => {
    let alive = true;
    setError(null);
    api
      .dashboard()
      .then((d) => alive && setData(d))
      .catch((err) => {
        if (!alive) return;
        setError(err instanceof ApiError ? err : new ApiError(String(err)));
      });
    return () => {
      alive = false;
    };
  }, [refreshKey, reload]);

  useEffect(() => {
    let alive = true;
    api
      .manutencaoVeiculos()
      .then((v) => alive && setManut(Array.isArray(v) ? v : []))
      .catch(() => alive && setManut([])); // acessório: se falhar, apenas não mostra
    return () => {
      alive = false;
    };
  }, [refreshKey, reload]);

  if (error)
    return (
      <ErrorState
        message={error.message}
        code={error.code}
        onRetry={() => setReload((n) => n + 1)}
      />
    );
  if (!data) return <div className="empty">Carregando indicadores...</div>;

  const c = data.cards;

  /**
   * De onde veio a nota de satisfação. O indicador soma a avaliação dos
   * serviços executados (lançada aqui) com a pesquisa de satisfação do cliente
   * (lançada na Qualidade) — antes cada sistema mostrava só a sua metade.
   */
  const origemSatisfacao = (): string => {
    if (c.satisfacao_integracao === "offline") {
      return "só os serviços avaliados aqui — a Qualidade não respondeu agora";
    }
    if (c.satisfacao_total_avaliacoes === 0) {
      return "nenhuma avaliação lançada ainda (nem serviço aqui, nem pesquisa na Qualidade)";
    }
    const partes: string[] = [];
    if (c.satisfacao_servicos > 0) partes.push(`${c.satisfacao_servicos} serviço(s) avaliado(s)`);
    if (c.satisfacao_pesquisas > 0) {
      partes.push(`${c.satisfacao_pesquisas} pesquisa(s) de cliente (Qualidade)`);
    }
    return partes.join(" + ");
  };

  // Cards na mesma ordem da lista de indicadores operacionais.
  const cards = [
    { label: "Treinamentos realizados", value: c.treinamentos_total, Icon: IconGraduation, tone: "green" },
    { label: "Média de treinamentos / colaborador", value: c.treinamentos_media, Icon: IconGraduation, tone: "emerald" },
    { label: "Índices de retrabalho (mês)", value: c.retrabalho_mes, Icon: IconRepeat, tone: "amber" },
    { label: "Média de retrabalho / colaborador", value: c.retrabalho_media, Icon: IconRepeat, tone: "amber" },
    { label: "Acidentes (mês)", value: c.acidentes_mes, Icon: IconHardHat, tone: "red" },
    { label: "Média de acidentes / colaborador", value: c.acidentes_media, Icon: IconHardHat, tone: "red" },
    {
      label: "Satisfação média do cliente",
      value: c.satisfacao_total_avaliacoes > 0 ? `${c.satisfacao_media} / 10` : "—",
      Icon: IconSmile,
      tone: "green",
      // De onde saiu a nota: os serviços avaliados aqui + a pesquisa de
      // satisfação lançada na Qualidade (as duas compõem o indicador).
      detalhe: origemSatisfacao(),
    },
    { label: "Atividades executadas", value: c.atividades_executadas, Icon: IconList, tone: "lime" },
    { label: "Obras ativas", value: c.obras_ativas, Icon: IconBuilding, tone: "teal" },
    { label: "Equipamentos p/ calibração", value: c.equipamentos_calibracao, Icon: IconGauge, tone: "amber" },
    { label: "Equipamentos liberados", value: c.equipamentos_liberados, Icon: IconClipboardCheck, tone: "emerald" },
  ];

  const calibColors = data.equipamentosPorSituacao.map((d) => CALIB_CORES[d.situacao] ?? "#94a3b8");
  const libColors = data.liberacoesPorStatus.map((d) => LIB_CORES[d.status] ?? "#94a3b8");

  return (
    <div className="dashboard">
      <div className="cards">
        {cards.map((card) => (
          <div
            className={`card tone-${card.tone}`}
            key={card.label}
            title={"detalhe" in card ? String(card.detalhe) : undefined}
          >
            <div className="card-icon">
              <card.Icon size={22} />
            </div>
            <div className="card-body">
              <span className="card-value">{card.value}</span>
              <span className="card-label">{card.label}</span>
              {"detalhe" in card && <span className="card-origem">{String(card.detalhe)}</span>}
            </div>
          </div>
        ))}
      </div>

      {manut.length > 0 && (
        <div className="maint-panel">
          <div className="maint-panel-head">
            <span className="maint-panel-icon" aria-hidden="true">🔧</span>
            <h3>Veículos que precisam de manutenção</h3>
            <span className="maint-panel-count">{manut.length}</span>
          </div>
          <div className="maint-list">
            {manut.map((v) => (
              <div className="maint-item" key={v.id}>
                <div className="maint-item-main">
                  <span className="maint-item-placa">{v.placa || v.modelo || `#${v.id}`}</span>
                  {v.modelo && v.placa ? <span className="maint-item-modelo">{v.modelo}</span> : null}
                </div>
                <span className="maint-item-resp">
                  {v.colaborador_nome || "Sem responsável"}
                </span>
                <span className={`pill pill-${statusTone(v.situacao_manutencao)}`}>
                  {v.situacao_manutencao}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="charts">
        <ChartBox title="Treinamentos por colaborador">
          <BarChart
            data={data.treinamentosPorColaborador}
            xKey="colaborador"
            series={[{ key: "treinamentos", label: "Treinamentos", color: "#0c7a3f" }]}
            refLine={{ value: c.treinamentos_media, label: `média ${c.treinamentos_media}` }}
          />
        </ChartBox>

        <ChartBox title="Índices de retrabalho por colaborador">
          <BarChart
            data={data.retrabalhoPorColaborador}
            xKey="colaborador"
            series={[{ key: "quantidade", label: "Retrabalho", color: "#c98a14" }]}
            refLine={{ value: c.retrabalho_media, label: `média ${c.retrabalho_media}` }}
          />
        </ChartBox>

        <ChartBox title="Índices de acidentes por colaborador">
          <BarChart
            data={data.acidentesPorColaborador}
            xKey="colaborador"
            series={[{ key: "quantidade", label: "Acidentes", color: "#dc2626" }]}
            refLine={{ value: c.acidentes_media, label: `média ${c.acidentes_media}` }}
          />
        </ChartBox>

        <ChartBox title="Obras por colaborador">
          <BarChart
            data={data.obrasPorColaborador}
            xKey="colaborador"
            series={[{ key: "obras", label: "Obras", color: "#0e9488" }]}
          />
        </ChartBox>

        <ChartBox title="Nível de satisfação do cliente">
          <PieChart
            data={data.satisfacaoPorNivel}
            valueKey="total"
            labelKey="nivel"
            colors={COLORS}
          />
        </ChartBox>

        <ChartBox title="Total de atividades por status">
          <PieChart
            data={data.atividadesPorStatus}
            valueKey="total"
            labelKey="status"
            colors={["#0c7a3f", "#15a85a", "#34d27e", "#c98a14", "#dc2626", "#94a3b8"]}
          />
        </ChartBox>

        <ChartBox title="Calibração de equipamentos de ensaio">
          <PieChart
            data={data.equipamentosPorSituacao}
            valueKey="total"
            labelKey="situacao"
            colors={calibColors}
          />
        </ChartBox>

        <ChartBox title="Liberação de equipamentos por status">
          <PieChart
            data={data.liberacoesPorStatus}
            valueKey="total"
            labelKey="status"
            colors={libColors}
          />
        </ChartBox>
      </div>
    </div>
  );
}

function ChartBox({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="chart-box">
      <h3>{title}</h3>
      {children}
    </div>
  );
}
